// European Portuguese date formatting, consistent across the app.

const longFormatter = new Intl.DateTimeFormat("pt-PT", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** "21 de junho de 2025" from an ISO date (YYYY-MM-DD). */
export function longDate(iso: string): string {
  // Read as a local date (avoids the timezone shift of `new Date("YYYY-MM-DD")`).
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return longFormatter.format(new Date(year, month - 1, day));
}

const monthYearFormatter = new Intl.DateTimeFormat("pt-PT", { month: "long", year: "numeric" });
const weekdayFormatter = new Intl.DateTimeFormat("pt-PT", { weekday: "long" });
const dayMonthFormatter = new Intl.DateTimeFormat("pt-PT", { day: "numeric", month: "long" });

/** "agosto de 2026" — the calendar's month heading (IDEA-030). */
export function monthYear(year: number, month1: number): string {
  return monthYearFormatter.format(new Date(year, month1 - 1, 1));
}

/** "21 de junho" — a date with no year, for recurring events. */
export function dayMonth(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return dayMonthFormatter.format(new Date(year, month - 1, day));
}

/** "sábado" from an ISO date. */
export function weekday(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return "";
  return weekdayFormatter.format(new Date(year, month - 1, day));
}

// ------------------------------------------------------------
//  Plain calendar arithmetic (IDEA-030)
//
//  All of it on YYYY-MM-DD strings and local Date objects built from their
//  parts — never `new Date("2026-08-18")`, which parses as UTC midnight and
//  lands on the day before for anyone west of Greenwich.
// ------------------------------------------------------------

/** Builds an ISO date (YYYY-MM-DD) from its parts. */
export function toIsoDate(year: number, month1: number, day: number): string {
  const mm = String(month1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

/** Splits an ISO date into its numeric parts. */
export function fromIsoDate(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split("-").map(Number);
  return { year, month, day };
}

/** Today in Portugal, as YYYY-MM-DD. */
export function todayIso(): string {
  const { year, month, day } = todayInLisbon();
  return toIsoDate(year, month, day);
}

/** How many days a month has (month is 1-based). */
export function daysInMonth(year: number, month1: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(year, month1, 0).getDate();
}

/**
 * Which weekday the 1st falls on, as an index into a **Monday-first** week
 * (0 = segunda … 6 = domingo). Portuguese calendars start on Monday, while
 * `getDay()` returns 0 for Sunday — hence the shift.
 */
export function firstWeekdayIndex(year: number, month1: number): number {
  return (new Date(year, month1 - 1, 1).getDay() + 6) % 7;
}

/** Whole days from `fromIso` to `toIso` (negative if `toIso` is earlier). */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = fromIsoDate(fromIso);
  const b = fromIsoDate(toIso);
  if (!a.year || !b.year) return 0;
  const start = new Date(a.year, a.month - 1, a.day);
  const end = new Date(b.year, b.month - 1, b.day);
  return Math.round((end.getTime() - start.getTime()) / 86_400_000);
}

/** Month labels, Monday-first, for the calendar header. */
export const WEEKDAY_LABELS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"] as const;

// ------------------------------------------------------------
//  Anniversary — the June 21 logic.
// ------------------------------------------------------------

/** Today's date in Portugal (Europe/Lisbon), as {year, month, day}. */
function todayInLisbon(): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Lisbon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [year, month, day] = parts.split("-").map(Number);
  return { year, month, day };
}

export interface AnniversaryState {
  /** Has this year's June 21 arrived (or passed)? */
  arrived: boolean;
  /** Days until the next June 21 (0 on the day itself). */
  daysRemaining: number;
  /** Which anniversary the NEXT occurrence is (1 = first). */
  years: number;
  /** Which anniversary to SHOW in the scene (the most recent one already reached). */
  yearsToShow: number;
}

/**
 * Works out the anniversary state from the official date (YYYY-MM-DD).
 * Uses the day/month of the official date as the day celebrated each year.
 */
export function anniversaryState(officialDateIso: string): AnniversaryState {
  const [officialYear, officialMonth, officialDay] = officialDateIso.split("-").map(Number);
  const { year, month, day } = todayInLisbon();

  // Compare dates as YYYYMMDD numbers (safe, no timezones involved).
  const asNumber = (y: number, m: number, d: number) => y * 10000 + m * 100 + d;
  const todayNum = asNumber(year, month, day);
  const thisYearNum = asNumber(year, officialMonth, officialDay);

  const arrived = todayNum >= thisYearNum;

  // The relevant occurrence's year: this year's (or next year's if it already passed).
  const targetYear = todayNum > thisYearNum ? year + 1 : year;
  const target = new Date(targetYear, officialMonth - 1, officialDay);
  const todayDate = new Date(year, month - 1, day);
  const daysRemaining = Math.max(
    0,
    Math.round((target.getTime() - todayDate.getTime()) / 86_400_000),
  );

  // Which anniversary the next occurrence is (targetYear − officialYear). In 2026 = 1st.
  const years = Math.max(1, targetYear - officialYear);

  // For the scene: the most recent anniversary that HAS arrived (this year if it
  // already passed, otherwise the previous one). So after 21 Jun 2026 it keeps
  // saying "primeiro".
  const yearsToShow = Math.max(1, (arrived ? year : year - 1) - officialYear);

  return { arrived, daysRemaining, years, yearsToShow };
}

/** "primeiro", "2.º", "3.º"… for the anniversary heading. */
export function anniversaryOrdinal(years: number): string {
  if (years === 1) return "primeiro";
  return `${years}.º`;
}

/**
 * Days from the official date (YYYY-MM-DD) until today, in Portugal.
 * Uses the same timezone-free logic as the anniversary (compares local dates).
 * Zero or negative is normalised to 0. (IDEA-002)
 */
export function daysTogether(officialDateIso: string): number {
  const [year, month, day] = officialDateIso.split("-").map(Number);
  if (!year || !month || !day) return 0;
  const start = new Date(year, month - 1, day);
  const t = todayInLisbon();
  const today = new Date(t.year, t.month - 1, t.day);
  return Math.max(0, Math.round((today.getTime() - start.getTime()) / 86_400_000));
}
